import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AppWindow,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Users,
} from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function ApplicationsAdminPage() {
  const [apps, setApps] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [clientCounts, setClientCounts] = useState({});
  const [form, setForm] = useState({ name: "", code: "", description: "" });
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [appResponse, organizationResponse] = await Promise.all([
        api.get("/applications", {
          params: { include_deleted: includeDeleted },
        }),
        api.get("/organizations"),
      ]);

      const appItems = appResponse.data || [];
      const organizationItems = organizationResponse.data || [];

      setApps(appItems);
      setOrganizations(organizationItems);

      const results = await Promise.allSettled(
        organizationItems.map(async (organization) => {
          const response = await api.get(
            `/organizations/${organization.id}/applications`
          );

          return {
            organizationId: organization.id,
            applications: response.data?.applications || [],
          };
        })
      );

      const counts = {};

      appItems.forEach((application) => {
        counts[application.id] = {
          count: 0,
          clients: [],
        };
      });

      results.forEach((result) => {
        if (result.status !== "fulfilled") return;

        const organization = organizationItems.find(
          (item) =>
            item.id === result.value.organizationId
        );

        result.value.applications.forEach((application) => {
          if (!application.assigned) return;

          if (!counts[application.id]) {
            counts[application.id] = {
              count: 0,
              clients: [],
            };
          }

          counts[application.id].count += 1;

          if (organization?.name) {
            counts[application.id].clients.push(
              organization.name
            );
          }
        });
      });

      setClientCounts(counts);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [includeDeleted]);

  const submit = async () => {
    const name = form.name.trim();
    const code = form.code.trim().toUpperCase();

    if (!name) {
      return toast.error("Application name is required");
    }

    if (!code) {
      return toast.error("Application code is required");
    }

    try {
      await api.post("/applications", {
        name,
        code,
        description: form.description.trim(),
        modules: [],
      });
      toast.success("Application created successfully");
      setForm({ name: "", code: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      toast.success("Application deleted successfully");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doRestore = async (id) => {
    try {
      await api.post(`/applications/${id}/restore`);
      toast.success("Application restored successfully");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="space-y-5"
        data-testid="admin-apps-page"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Administration
            </div>

            <h1 className="font-display text-3xl font-extrabold tracking-tight">
              Applications &amp; Modules
            </h1>

            <p className="mt-1 text-xs text-muted-foreground">
              Manage the application catalogue, client access and modules.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-xs text-muted-foreground">
              {apps.length} application
              {apps.length === 1 ? "" : "s"}
            </div>

            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Show Deleted
              </span>

              <Switch
                data-testid="apps-show-deleted"
                checked={includeDeleted}
                onCheckedChange={setIncludeDeleted}
              />
            </div>
          </div>
        </div>


        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">

          {/* Applications table */}
          <div className="overflow-hidden rounded-xl border border-border bg-card">

            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">

                <thead className="bg-secondary/20 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left">
                      Application
                    </th>

                    <th className="px-4 py-3 text-left">
                      Code
                    </th>

                    <th className="px-4 py-3 text-left">
                      Modules
                    </th>

                    <th className="px-4 py-3 text-left">
                      Clients
                    </th>

                    <th className="px-4 py-3 text-left">
                      Status
                    </th>

                    <th className="px-4 py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>


                <tbody>
                  {apps.map((app) => {
                    const clientInfo =
                      clientCounts[app.id] || {
                        count: 0,
                        clients: [],
                      };

                    return (
                      <tr
                        key={app.id}
                        data-row
                        data-testid={`app-row-${app.code}`}
                        className={`border-b border-border/70 last:border-b-0 ${
                          app.deleted ? "opacity-60" : ""
                        }`}
                      >
                        <td className="px-4 py-3.5">

                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                              <AppWindow className="h-4 w-4" />
                            </div>

                            <div className="min-w-0">
                              <div className="truncate font-semibold">
                                {app.name}
                              </div>

                              {app.description && (
                                <div className="mt-0.5 max-w-[320px] truncate text-[11px] text-muted-foreground">
                                  {app.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3.5">
                          <span className="rounded-md border border-border bg-muted/40 px-2 py-1 font-mono text-xs">
                            {app.code}
                          </span>
                        </td>

                        <td className="px-4 py-3.5 text-xs text-muted-foreground">
                          {(app.modules || []).length} module
                          {(app.modules || []).length === 1 ? "" : "s"}
                        </td>

                        <td className="px-4 py-3.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help items-center gap-1.5 rounded-md border border-border bg-secondary/30 px-2 py-1.5 text-xs">
                                <Users className="h-3.5 w-3.5 text-primary" />
                                {clientInfo.count} client
                                {clientInfo.count === 1 ? "" : "s"}
                              </span>
                            </TooltipTrigger>

                            <TooltipContent
                              side="top"
                              className="max-w-xs"
                            >
                              {clientInfo.clients.length > 0
                                ? clientInfo.clients.join(", ")
                                : "No clients currently assigned"}
                            </TooltipContent>
                          </Tooltip>
                        </td>

                        <td className="px-4 py-3.5">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                              app.deleted
                                ? "border-rose-500/30 bg-rose-500/10 text-rose-600"
                                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                            }`}
                          >
                            {app.deleted
                              ? "Deleted"
                              : app.status === "inactive"
                                ? "Inactive"
                                : "Active"}
                          </span>
                        </td>

                        <td className="px-4 py-3.5 text-right">

                          {!app.deleted ? (
                            <div className="inline-flex items-center gap-1.5">

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Link
                                    to={`/admin/applications/${app.id}`}
                                    data-testid={`app-edit-${app.code}`}
                                  >
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label={`Edit ${app.name}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </Link>
                                </TooltipTrigger>

                                <TooltipContent>
                                  Edit Application
                                </TooltipContent>
                              </Tooltip>


                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() =>
                                      setConfirmDelete(app)
                                    }
                                    data-testid={`app-delete-${app.code}`}
                                    className="h-8 w-8 border-rose-500/30 text-rose-600 hover:bg-rose-500/10"
                                    aria-label={`Delete ${app.name}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>

                                <TooltipContent>
                                  Delete Application
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() =>
                                    doRestore(app.id)
                                  }
                                  data-testid={`app-restore-${app.code}`}
                                  className="h-8 w-8"
                                  aria-label={`Restore ${app.name}`}
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>

                              <TooltipContent>
                                Restore Application
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })}


                  {apps.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-12 text-center"
                      >
                        <AppWindow className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />

                        <div className="text-sm font-semibold">
                          No Applications
                        </div>

                        <div className="mt-1 text-xs text-muted-foreground">
                          Create your first application using the form.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>


          {/* Create application */}
          <div className="h-fit space-y-4 rounded-xl border border-border bg-card p-5">

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                New Application
              </div>

              <h2 className="mt-1 font-display text-lg font-bold">
                Add Application
              </h2>

              <p className="mt-1 text-xs text-muted-foreground">
                Create an application before assigning it to clients.
              </p>
            </div>


            <div className="space-y-1.5">
              <Label>
                Name <span className="text-rose-500">*</span>
              </Label>

              <Input
                data-testid="app-name"
                value={form.name}
                onChange={(event) =>
                  setForm({
                    ...form,
                    name: event.target.value,
                  })
                }
              />
            </div>


            <div className="space-y-1.5">
              <Label>
                Code <span className="text-rose-500">*</span>
              </Label>

              <Input
                data-testid="app-code"
                value={form.code}
                onChange={(event) =>
                  setForm({
                    ...form,
                    code: event.target.value.toUpperCase(),
                  })
                }
                placeholder="ERP"
              />
            </div>


            <div className="space-y-1.5">
              <Label>Description</Label>

              <Textarea
                data-testid="app-description"
                rows={3}
                value={form.description}
                onChange={(event) =>
                  setForm({
                    ...form,
                    description: event.target.value,
                  })
                }
              />
            </div>


            <Button
              data-testid="app-create"
              onClick={submit}
              className="w-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Application
            </Button>
          </div>
        </div>


        <ConfirmDialog
          open={!!confirmDelete}
          onOpenChange={(open) =>
            !open && setConfirmDelete(null)
          }
          title={
            confirmDelete
              ? `Delete "${confirmDelete.name}"?`
              : "Delete?"
          }
          description="Soft delete — the application will be hidden. If linked to open tickets, deletion will be blocked."
          confirmLabel="Delete"
          destructive
          onConfirm={() =>
            confirmDelete &&
            doDelete(confirmDelete.id)
          }
          testid="confirm-delete-app"
        />
      </div>
    </TooltipProvider>
  );
}
